import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getNewsArticles, getPortfolioItemsWithContent } from '../sanity/client';
import { useCachedTexts } from '../hooks/useCachedTexts';
import './RecentArticles.css';

const RecentArticles = () => {
  const { t, i18n } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getText } = useCachedTexts('home_latest_news');

  // Helper to get localized title
  const getLocalizedTitle = (item) => {
    const lang = i18n.language === 'it' ? 'It' : 'En';
    // Both articles and projects now have localized titles
    return item[`title${lang}`] || item.titleEn || item.title;
  };

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const [newsData, projectsData] = await Promise.all([
          getNewsArticles(),
          getPortfolioItemsWithContent()
        ]);
        
        // Combine articles and projects with type markers
        const articlesWithType = (newsData || []).map(article => ({
          ...article,
          type: 'article',
          date: article.publishedDate
        }));
        
        const projectsWithType = (projectsData || []).map(project => ({
          ...project,
          type: 'project',
          date: project.publishedDate
        }));
        
        // Combine and sort by date, take top 3
        const combined = [...articlesWithType, ...projectsWithType]
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 3);
        
        setArticles(combined);
      } catch (error) {
        console.error('Error fetching recent articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <section className="recent-articles-section">
        <div className="recent-articles-container">
          <div className="recent-articles-header">
            <h2>{getText('heading', t('home.latestNews'))}</h2>
          </div>
          <div className="recent-articles-list">
            {[1, 2, 3].map((i) => (
              <div key={i} className="recent-article-item skeleton">
                <div className="skeleton-date" />
                <div className="skeleton-title" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (articles.length === 0) {
    return null;
  }

  return (
    <section className="recent-articles-section">
      <div className="recent-articles-container">
        <div className="recent-articles-header">
          <h2>{getText('heading', t('home.latestNews'))}</h2>
          <Link to="/news" className="view-all-link">
            {getText('view_all', t('home.viewAll'))}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>
        
        <ul className="recent-articles-list">
          {articles.map((item) => (
            <li key={item._id} className={`recent-article-item ${item.type === 'project' ? 'recent-article-project' : ''}`}>
              <Link 
                to={item.type === 'article' ? `/news/${item.slug}` : `/portfolio/${item.slug}`} 
                className="recent-article-link"
              >
                {item.type === 'project' && (
                  <span className="recent-article-badge">{getText('featured_project', t('news.featuredProject'))}</span>
                )}
                <span className="recent-article-date">{formatDate(item.date)}</span>
                <span className="recent-article-title">{getLocalizedTitle(item)}</span>
                <svg 
                  className="recent-article-arrow" 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default RecentArticles;
